import axios from 'axios';
import moment from 'moment-timezone';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  fetchScheduledMessages,
  cancelScheduledMessage,
  updateScheduledMessage,
} from '../reducers/ScheduledMessageReducer';
import ScheduledMessageManager from '../components/ScheduledMessageManager';
import PageLoader from '../components/PageLoader';
import { calculatePastDateRange, calculateFutureDateRange } from '../helpers/AppointmentHelpers';
import { NAVIGATION_TABS } from '../constants/AppointmentManagerConstants';
import { DataHelpers, SearchHelpers, UserHelpers } from '../helpers';
import NotificationService from '../services/NotificationService';

const STATUS_OPTIONS = [
  { id: '', label: 'All Statuses' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'alert', label: 'Alert' },
  { id: 'sent', label: 'Sent' },
  { id: 'failed', label: 'Failed' },
  { id: 'cancelled', label: 'Cancelled' },
];

class ScheduledMessageManagerContainer extends Component {
  state = {
    activeTab: NAVIGATION_TABS.upcomingAppointment,
    sort: 'asc',
    status: '',
    alertOnly: false,
    dateFrom: moment().startOf('day'),
    dateTo: calculateFutureDateRange(6).endDate,
    activeKey: 3,
    isDeleteModalOpen: false,
    deletingId: null,
    isEditModalOpen: false,
    editingMessage: null,
    editingPhones: [],
    editingPhoneId: null,
    members: {},
    membersIds: [],
    selectedMemberIds: [],
    selectedMembers: {},
    contacts: {},
    contactsIds: [],
    selectedContactIds: [],
    selectedContacts: {},
  };

  componentDidMount() {
    this.fetchData();
    this.fetchFilterData(null, 'members');
    this.fetchFilterData(null, 'contacts');
  }

  fetchData = () => {
    const { status, alertOnly, dateFrom, dateTo, sort, selectedMemberIds, selectedContactIds, activeTab } = this.state;
    const isUpcoming = activeTab === NAVIGATION_TABS.upcomingAppointment;
    const filters = {};
    if (status) {
      filters.status = status;
    }
    if (alertOnly) filters.alertOnly = true;
    const effectiveDateFrom = isUpcoming ? moment() : dateFrom;
    if (effectiveDateFrom) filters.dateFrom = moment(effectiveDateFrom).utc().format('YYYY-MM-DD HH:mm:ss');
    if (dateTo) filters.dateTo = moment(dateTo).utc().format('YYYY-MM-DD HH:mm:ss');
    if (sort) filters.sort = sort;
    if (selectedMemberIds.length) filters.senderIds = selectedMemberIds;
    if (selectedContactIds.length) filters.toUserIds = selectedContactIds;
    this.props.fetchScheduledMessages(filters);
  };

  handleTabChange = (tab) => {
    const isUpcoming = tab === NAVIGATION_TABS.upcomingAppointment;
    const dates = isUpcoming ? calculateFutureDateRange(6) : calculatePastDateRange(6);
    this.setState({
      activeTab: tab,
      sort: isUpcoming ? 'asc' : 'desc',
      status: '',
      alertOnly: false,
      dateFrom: isUpcoming ? moment().startOf('day') : dates.startDate,
      dateTo: dates.endDate,
      activeKey: 3,
      selectedMemberIds: [],
      selectedMembers: {},
      selectedContactIds: [],
      selectedContacts: {},
    }, this.fetchData);
  };

  fetchFilterData = (searchText, filterName) => {
    const scope = filterName === 'contacts' ? 'nonMembers' : filterName;
    const url = searchText
      ? SearchHelpers.getSearchUrl({ search: searchText, scope, includeInactiveAndDeleted: true, type: 'name' })
      : `/users/allMembersOrContacts?userType=${filterName}`;

    axios.get(url).then((response) => {
      const userResponse = searchText ? response.data?.users : response.data;
      const filtered = DataHelpers.arrayToObj(userResponse || [], 'id');
      const ids = (userResponse || []).map((item) => item.id);

      Object.keys(filtered).forEach((id) => {
        filtered[id].name = UserHelpers.formatAvatarName(filtered[id].firstName, filtered[id].lastName);
        filtered[id].title = (filtered[id].firstName && filtered[id].lastName)
          ? `${filtered[id].firstName} ${filtered[id].lastName}`
          : (filtered[id].firstName || filtered[id].lastName || '');
      });

      if (filterName === 'contacts') {
        this.setState({ contacts: filtered, contactsIds: ids });
      } else {
        this.setState({ members: filtered, membersIds: ids });
      }
    }).catch(() => {});
  };

  handleUpdateSelectedIds = (selectedIds, selectedUsers, filterName) => {
    if (filterName === 'contacts') {
      this.setState({ selectedContactIds: selectedIds, selectedContacts: selectedUsers }, this.fetchData);
    } else {
      this.setState({ selectedMemberIds: selectedIds, selectedMembers: selectedUsers }, this.fetchData);
    }
  };

  handleClearSelectedMembers = () => {
    this.setState({ selectedMemberIds: [], selectedMembers: {} }, this.fetchData);
  };

  handleClearSelectedContacts = () => {
    this.setState({ selectedContactIds: [], selectedContacts: {} }, this.fetchData);
  };

  handleDateChange = ({ startDate, endDate, activeKey }) => {
    let start = startDate;
    let end = endDate;
    if (activeKey === 5) {
      start = moment(startDate).startOf('day');
      end = moment(endDate).endOf('day');
    }
    this.setState({ dateFrom: start, dateTo: end, activeKey }, this.fetchData);
  };

  handleStatusChange = (status) => {
    this.setState({ status }, this.fetchData);
  };

  handleAlertToggle = (alertOnly) => {
    this.setState({ alertOnly }, this.fetchData);
  };

  handleDeleteClick = (id) => {
    this.setState({ isDeleteModalOpen: true, deletingId: id });
  };

  handleDeleteConfirm = () => {
    this.props.cancelScheduledMessage(this.state.deletingId);
    this.setState({ isDeleteModalOpen: false, deletingId: null });
  };

  handleDeleteCancel = () => {
    this.setState({ isDeleteModalOpen: false, deletingId: null });
  };

  fetchEditingPhones = async (toUserId) => {
    try {
      const response = await axios.get(`/users/${toUserId}`);
      return (response.data?.phones || []).filter((p) => Number(p.ownerId) === Number(toUserId));
    } catch (e) {
      return [];
    }
  };

  handleEditClick = (message) => {
    this.setState({ editingMessage: message, editingPhones: [], editingPhoneId: message.phoneId || null }, () => {
      this.setState({ isEditModalOpen: true });
    });
    this.fetchEditingPhones(message.toUserId).then((phones) => this.setState({ editingPhones: phones }));
  };

  handleRescheduleClick = (message) => {
    this.setState({ editingMessage: { ...message, _isReschedule: true }, editingPhones: [], editingPhoneId: message.phoneId || null }, () => {
      this.setState({ isEditModalOpen: true });
    });
    this.fetchEditingPhones(message.toUserId).then((phones) => this.setState({ editingPhones: phones }));
  };

  handleEditConfirm = (scheduledAt, messageText, phoneId) => {
    const isReschedule = this.state.editingMessage._isReschedule;
    this.props.updateScheduledMessage(this.state.editingMessage.id, { scheduledAt, messageText, phoneId })
      .then((response) => {
        NotificationService('updateScheduledMessage', response || { status: 200 });
        if (isReschedule) this.fetchData();
      });
    this.setState({ isEditModalOpen: false, editingMessage: null, editingPhones: [], editingPhoneId: null });
  };

  handleEditClose = () => {
    this.setState({ isEditModalOpen: false, editingMessage: null, editingPhones: [], editingPhoneId: null });
  };

  navigateToConversation = (userId) => {
    this.props.history.push(`/inbox/all/user/${userId}`);
  };

  render() {
    if (this.props.isLoading) {
      return <PageLoader />;
    }

    return (
      <ScheduledMessageManager
        scheduledMessages={this.props.scheduledMessages}
        status={this.state.status}
        alertOnly={this.state.alertOnly}
        dateFrom={this.state.dateFrom}
        dateTo={this.state.dateTo}
        activeKey={this.state.activeKey}
        statusOptions={STATUS_OPTIONS}
        isDeleteModalOpen={this.state.isDeleteModalOpen}
        isEditModalOpen={this.state.isEditModalOpen}
        editingMessage={this.state.editingMessage}
        editingPhones={this.state.editingPhones}
        editingPhoneId={this.state.editingPhoneId}
        members={this.state.members}
        membersIds={this.state.membersIds}
        selectedMemberIds={this.state.selectedMemberIds}
        selectedMembers={this.state.selectedMembers}
        contacts={this.state.contacts}
        contactsIds={this.state.contactsIds}
        selectedContactIds={this.state.selectedContactIds}
        selectedContacts={this.state.selectedContacts}
        onStatusChange={this.handleStatusChange}
        onAlertToggle={this.handleAlertToggle}
        onDateChange={this.handleDateChange}
        onDeleteClick={this.handleDeleteClick}
        onDeleteConfirm={this.handleDeleteConfirm}
        onDeleteCancel={this.handleDeleteCancel}
        onEditClick={this.handleEditClick}
        onEditConfirm={this.handleEditConfirm}
        onEditClose={this.handleEditClose}
        onRescheduleClick={this.handleRescheduleClick}
        activeTab={this.state.activeTab}
        onTabChange={this.handleTabChange}
        onUpdateSelectedIds={this.handleUpdateSelectedIds}
        onClearSelectedMembers={this.handleClearSelectedMembers}
        onClearSelectedContacts={this.handleClearSelectedContacts}
        onFetchFilterData={this.fetchFilterData}
        navigateToConversation={this.navigateToConversation}
      />
    );
  }
}

ScheduledMessageManagerContainer.propTypes = {
  scheduledMessages: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  fetchScheduledMessages: PropTypes.func.isRequired,
  cancelScheduledMessage: PropTypes.func.isRequired,
  updateScheduledMessage: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  scheduledMessages: state.scheduledMessage.scheduledMessages,
  isLoading: state.scheduledMessage.isLoading,
});

const actions = {
  fetchScheduledMessages,
  cancelScheduledMessage,
  updateScheduledMessage,
};

export default withRouter(connect(mapStateToProps, actions)(ScheduledMessageManagerContainer));
